import {defineStore} from "pinia"
import {addDay, format} from "@formkit/tempo"
import {fa} from "@formkit/i18n";

export const useFMStore = defineStore("FMStore", {

    state: () => ({
        selectedPostalCode: null,
        selectedWorkOrder: null,
        selectedDate: null,
        selectedTimeSlot: null,
        connected: true,
        stepNumber: null,
        error: false,
        errorMessage: null,
        postalCodes: [],
        loading: false,
        workOrders: [],
        openTimeSlots: [],
        blackoutDates: [],
        daysindaterange: 120,
        customerData: null,
        booked: false,
        appointment: null,
        stepHistory: [],
        currentStep: 'Welcome',
        isReschedule: false,
        showCancelMsg: false,
        rescheduleStarted: false,

    }),

    getters: {
        getCalendarDates() {
            let dates = [];
            const startdate = addDay(new Date(), 1)
            const enddate = addDay(new Date(), this.daysindaterange)
            while (startdate <= enddate) {
                dates.push(startdate.toISOString().split('T')[0]);
                startdate.setDate(startdate.getDate() + 1);
            }
            console.log('getCalendarDates', dates)
            return dates;
        },

        getOpenTimeSlotDates() {
            const dates = this.openTimeSlots.map(slot => slot.TimeSlotStart.split('T')[0])
            const uniqueDates = [...new Set(dates)]
            console.log('getOpenTimeSlotDates', uniqueDates)
            return uniqueDates;
        },

        getBlackoutDates() {
            const dates = [];
            const filteredWindows = this.blackoutDates.filter(window => window.Cycle === this.selectedWorkOrder.ReadCycle);
            console.log('Filtered Blackout Windows:', filteredWindows);

            filteredWindows.forEach(window => {
                const start = new Date(window.StartWindowDate);
                const end = new Date(window.EndWindowDate);
                while (start <= end) {
                    dates.push(format(start, "YYYY-MM-DD"));
                    //console.log(format(start, "YYYY-MM-DD"))
                    start.setDate(start.getDate() + 1);
                }
            });

            console.log('getBlackoutDates', dates);
            return dates;
        }
    },

    actions: {
        async getUnavailableDates() {
            let calendarDates = [];
            let openTimeSlotDates = [];
            let blackoutDates = [];
            let unavailableDates = [];

            calendarDates = this.getCalendarDates
            blackoutDates = this.getBlackoutDates
            openTimeSlotDates = this.getOpenTimeSlotDates


            // // First, filter out dates that are not in availableDates
            unavailableDates = calendarDates.filter(date => !openTimeSlotDates.includes(date));

            // // Next, add the dates from blackoutDates that are within the calendarDates range
            const blackoutWithinCalendar = blackoutDates.filter(date => calendarDates.includes(date));

            // // Combine both unavailable dates and blackout dates, ensuring no duplicates
            unavailableDates = [...new Set([...unavailableDates, ...blackoutWithinCalendar])];

            console.log('getUnavailableDates', unavailableDates);
            return unavailableDates;
        },

        async resetError() {
            this.error = false
            this.errorMessage = null
        },

        async getWorkOrders(PostalCode) {
            this.selectedPostalCode = PostalCode;
            try {
                this.loading = true;
                const data = await $fetch('/api/workorders', {
                    params: {
                        PremisePostalCode: PostalCode
                    }
                });
                this.workOrders = data;
                this.loading = false;
            } catch (e) {
                this.error = true;
                this.errorMessage = e.message;
                console.error('Error fetching work orders:', e);
            }
        },
        async getWorkOrder(id) {
            try {
                this.loading = true;
                const data = await $fetch(`/api/workorder/${id}`);
                this.selectedWorkOrder = data;
                this.loading = false;
                return data;
            } catch (e) {
                this.error = true;
                this.errorMessage = e.message;
                console.error('Error fetching work order:', e);
            }
        },

        async getPostalCodes() {
            try {
                this.loading = true
                const data = await $fetch('/api/postalcodes')
                this.postalCodes = data;
                this.loading = false
            } catch (e) {
                this.error = true;
                this.errorMessage = e.message;
                console.error('Error fetching postal codes:', e);
            }
        },

        async getAvailability() {
            if (!this.selectedWorkOrder || !this.selectedWorkOrder.Area) {
                this.error = true;
                this.errorMessage = "Work order with an area must be selected first.";
                console.error(this.errorMessage);
                return;
            }
            try {
                this.loading = true;
                const data = await $fetch("/api/availability", {
                    params: {
                        area: this.selectedWorkOrder.Area,
                    },
                });
                console.log('------------------------------------------')
                console.log(data)
                this.openTimeSlots = data;
                this.loading = false;
            } catch (e) {
                this.error = true;
                this.errorMessage = e.message;
                this.loading = false; // Ensure loading is turned off on error
                console.error("Error fetching openTimeSlots:", e);
            }
        },

        async getBlackoutSchedule() {
            try {
                this.loading = true;
                const data = await $fetch('/api/blackout');
                this.blackoutDates = data;
                this.loading = false;
            } catch (e) {
                this.error = true;
                this.errorMessage = e.message;
                console.error('Error fetching blackoutSchedules:', e);
            }
        },

        async checkDB() {
            try {
                const data = await $fetch('/api/dbcheck');
                console.log('data', data);
                if (data === true) {
                    this.connected = true;
                    this.error = false;
                    this.errorMessage = '';
                } else {
                    this.connected = false;
                    this.error = true;
                    this.errorMessage = 'Database connection failed';
                }
            } catch (e) {
                this.connected = false;
                this.error = true;
                this.errorMessage = e.message;
                console.error('Error fetching openTimeSlots:', e);
            }
        },


        async createAppointment() {
            // Ensure the selected time slot ID is a string
            const selectedTimeSlotId = String(this.selectedTimeSlot);

            // Find the selected time slot data
            const selectedSlotData = this.openTimeSlots.find(slot => String(slot.Id) === selectedTimeSlotId);

            if (!selectedSlotData) {
                this.error = true;
                this.errorMessage = 'Selected time slot not found';
                console.error('Error: Selected time slot not found');
                return;
            }

            console.log(selectedSlotData);

            const appointmentData = {
                WorkorderId: this.selectedWorkOrder.Id,
                TimeSlot: selectedSlotData.TimeSlotName,
                AppointmentDate: this.selectedDate,
                AppointmentStartTime: selectedSlotData.TimeSlotStart,
                AppointmentEndTime: selectedSlotData.TimeSlotEnd,
                AppointmentNote: this.customerData?.note || '',
                CreatedBy: this.customerData?.ScheduledBy || "anonymous",
                CustomerData: this.customerData
            };

            console.log('appointmentData', appointmentData);

            const serializedData = JSON.stringify({appointmentData});
            console.log('Serialized appointmentData:', serializedData);

            try {
                const response = await $fetch('/api/fmappointment', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: serializedData, // Ensure JSON serialization
                });

                if (response.success) {
                    console.log('Appointment created successfully:', response.data)
                    this.booked = true;
                    return response;
                } else {
                    throw new Error(response.message || 'Failed to create appointment');
                }
            } catch (error) {
                this.error = true;
                this.errorMessage = error.message;
                console.error('Error creating appointment:', error);
            }
        },

        async getAppointmentById(appointmentId) {
            try {
                this.loading = true;
                this.error = false;
                this.errorMessage = '';

                // Fetch the appointment details by ID
                const response = await $fetch(`/api/appointments/${appointmentId}`);
                console.log(response)
                // Check if the API response indicates success
                if (response && response.success && response.data && response.data.Id) {
                    // Set the appointment in the store state if valid record is returned

                    this.appointment = response.data;
                    console.log('Appointment found:', this.appointment);
                    return response.data;
                } else {
                    // If no valid appointment record, throw an error
                    throw new Error(response.message || 'Appointment not found or invalid response.');
                }
                this.loading = false;
            } catch (e) {
                // Ensure loading is set to false and handle the error
                this.loading = false;
                this.error = true;
                this.errorMessage = e.message || 'Error fetching appointment details';
                console.error('Error fetching appointment:', e);
                throw new Error(e.message || 'Appointment not found or invalid response.');

            }
        },

        async cancelAppointment() {
            const serializedData = {
                "AppointmentId": Number(this.appointment.Id)
            };

            console.log('serializedData', serializedData);

            try {
                this.loading = true;
                this.error = false;
                this.errorMessage = '';

                const response = await $fetch('/api/cancelappointment', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: serializedData, // Ensure JSON serialization
                });


                if (!response.success) {
                    // If the API response indicates failure
                    throw new Error(response.message || 'Error canceling the appointment.');
                }

                // Successfully canceled the appointment
                console.log('Appointment canceled:', response);
                this.appointment = null;
                this.loading = false;

            } catch (e) {
                // Handle both HTTP and network errors
                this.loading = false;
                this.error = true;
                this.errorMessage = e.message || 'Error canceling appointment details';
                console.error('Error canceling appointment:', e);
            }
        },
        reset() {
            this.postalCodes = [];
            this.selectedPostalCode = null;
            this.workOrders = [];
            this.selectedWorkOrder = null;
            this.selectedTimeSlot = null;
            this.booked = false;
        },

        goToStep(step) {
            if (this.currentStep) {
                this.stepHistory.push(this.currentStep)
            }
            this.currentStep = step
        },

        goBack() {
            if (this.stepHistory.length > 0) {
                this.currentStep = this.stepHistory.pop()
                this.stepNumber--;
            }
        },

        resetFlow() {
            this.stepHistory = [];
            this.currentStep = 'Welcome';
            this.postalCodes = [];
            this.selectedPostalCode = null;
            this.workOrders = [];
            this.selectedWorkOrder = null;
            this.selectedTimeSlot = null;
            this.booked = false;
        },

    }

});